import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Hook for debounced toast notifications to prevent too many notifications
 * @param delay - Debounce delay in milliseconds (default: 500ms)
 * @returns Function to show debounced toast notifications, with a `cancel()` that
 *          drops a still-pending one (a later event can reveal it as unwanted)
 */
export const useDebouncedToast = (delay: number = 500) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingToastRef = useRef<{
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
    options?: Parameters<typeof toast.success>[1];
  } | null>(null);

  const debouncedToast = useCallback(
    (
      type: 'success' | 'error' | 'info' | 'warning',
      message: string,
      options?: Parameters<typeof toast.success>[1],
    ) => {
      // Store the latest toast request
      pendingToastRef.current = { type, message, options };

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule toast after delay
      timeoutRef.current = setTimeout(() => {
        const pending = pendingToastRef.current;
        if (pending) {
          switch (pending.type) {
            case 'success':
              toast.success(pending.message, pending.options);
              break;
            case 'error':
              toast.error(pending.message, pending.options);
              break;
            case 'info':
              toast.info(pending.message, pending.options);
              break;
            case 'warning':
              toast.warning(pending.message, pending.options);
              break;
          }
          pendingToastRef.current = null;
        }
        timeoutRef.current = null;
      }, delay);
    },
    [delay],
  );

  /** Drop a scheduled-but-not-yet-shown toast. */
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingToastRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useMemo(() => Object.assign(debouncedToast, { cancel }), [debouncedToast, cancel]);
};
